/**
 * Client-side route ordering: nearest-neighbor seed + 2-opt improvement.
 * Uses haversine distances; no paid routing APIs.
 *
 * When any stop is `eveningPreferred` (DSNY / exterior sanitation), remaining
 * jobs are optimized in two bands: non-DSNY first (morning/afternoon), then
 * DSNY (evening). Geographic NN+2-opt still runs inside each band.
 */

export type RouteStop = {
  id: string
  latitude: number | null | undefined
  longitude: number | null | undefined
  /** DSNY / exterior sanitation — prefer later in the route. */
  eveningPreferred?: boolean
  /** Optional HH:MM[:SS]; used as a tiebreaker / uncoded-stop sort. */
  scheduledStartTime?: string | null
}

/** Fields used to classify a job as DSNY-evening vs hallway/earlier. */
export type DsnyEveningJobHints = {
  title?: string | null
  serviceType?: string | null
  dsnyIntegration?: boolean | null
  dsnyPickupDays?: unknown
}

const HALLWAY_TITLE_RE = /\bhallway\b|\binterior\s*clean/i
const DSNY_TITLE_RE =
  /\bdsny\b|\bgarbage\b|\brecycling\b|\borganics\b|\bbulk\b|\bexterior\s*(sanitation|maintenance|pickup)/i

function hasPickupDays(days: unknown): boolean {
  return Array.isArray(days) && days.length > 0
}

/**
 * DSNY / exterior sanitation jobs belong in the evening band.
 * Hallway / interior cleaning stays in the earlier band even on DSNY contracts
 * (jobs inherit contract `service_type` and sometimes `dsny_integration`).
 */
export function isDsnyEveningJob(job: DsnyEveningJobHints): boolean {
  const title = job.title || ''
  if (HALLWAY_TITLE_RE.test(title)) return false
  if (job.dsnyIntegration === true) return true
  if (DSNY_TITLE_RE.test(title)) return true
  if (hasPickupDays(job.dsnyPickupDays)) return true
  return false
}

function parseStartMinutes(value?: string | null): number | null {
  if (!value || !/^\d{1,2}:\d{2}/.test(value.trim())) return null
  const [h, m] = value.trim().split(':')
  const hours = Number(h)
  const mins = Number(m)
  if (!Number.isFinite(hours) || !Number.isFinite(mins)) return null
  return hours * 60 + mins
}

function startMinutesOrInf(stop: RouteStop): number {
  return parseStartMinutes(stop.scheduledStartTime) ?? Number.POSITIVE_INFINITY
}

const EARTH_KM = 6371

export function haversineKm(
  lat1: number,
  lon1: number,
  lat2: number,
  lon2: number
): number {
  const toRad = (d: number) => (d * Math.PI) / 180
  const dLat = toRad(lat2 - lat1)
  const dLon = toRad(lon2 - lon1)
  const a =
    Math.sin(dLat / 2) ** 2 +
    Math.cos(toRad(lat1)) * Math.cos(toRad(lat2)) * Math.sin(dLon / 2) ** 2
  return 2 * EARTH_KM * Math.asin(Math.min(1, Math.sqrt(a)))
}

function stopDistance(a: RouteStop, b: RouteStop): number {
  if (
    a.latitude == null ||
    a.longitude == null ||
    b.latitude == null ||
    b.longitude == null
  ) {
    return Number.POSITIVE_INFINITY
  }
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude)
}

function pathLength(stops: RouteStop[]): number {
  let total = 0
  for (let i = 0; i < stops.length - 1; i++) {
    const d = stopDistance(stops[i], stops[i + 1])
    if (!Number.isFinite(d)) return Number.POSITIVE_INFINITY
    total += d
  }
  return total
}

/** Nearest-neighbor tour starting at stops[0] (keeps first stop fixed). */
function nearestNeighbor(stops: RouteStop[]): RouteStop[] {
  if (stops.length <= 2) return [...stops]
  const remaining = stops.slice(1)
  const ordered: RouteStop[] = [stops[0]]
  while (remaining.length > 0) {
    const last = ordered[ordered.length - 1]
    let bestIdx = 0
    let bestDist = Number.POSITIVE_INFINITY
    let bestTime = Number.POSITIVE_INFINITY
    for (let i = 0; i < remaining.length; i++) {
      const d = stopDistance(last, remaining[i])
      const t = startMinutesOrInf(remaining[i])
      if (d < bestDist - 1e-9 || (Math.abs(d - bestDist) < 1e-9 && t < bestTime)) {
        bestDist = d
        bestTime = t
        bestIdx = i
      }
    }
    ordered.push(remaining.splice(bestIdx, 1)[0])
  }
  return ordered
}

/** 2-opt improvement; keeps index 0 fixed as the start. */
function twoOpt(stops: RouteStop[]): RouteStop[] {
  if (stops.length <= 3) return stops
  let best = [...stops]
  let improved = true
  while (improved) {
    improved = false
    const n = best.length
    for (let i = 1; i < n - 1; i++) {
      for (let k = i + 1; k < n; k++) {
        const next = best.slice(0, i).concat(best.slice(i, k + 1).reverse(), best.slice(k + 1))
        if (pathLength(next) + 1e-9 < pathLength(best)) {
          best = next
          improved = true
        }
      }
    }
  }
  return best
}

export type LatLng = { latitude: number; longitude: number }

const ORIGIN_ID = '__route_origin__'

function isFiniteLatLng(lat: number | null | undefined, lng: number | null | undefined): boolean {
  return lat != null && lng != null && Number.isFinite(lat) && Number.isFinite(lng)
}

function lastStopWithCoords(
  orderedIds: string[],
  byId: Map<string, RouteStop>
): LatLng | null {
  for (let i = orderedIds.length - 1; i >= 0; i--) {
    const stop = byId.get(orderedIds[i])
    if (stop && isFiniteLatLng(stop.latitude, stop.longitude)) {
      return { latitude: stop.latitude as number, longitude: stop.longitude as number }
    }
  }
  return null
}

function sortUncoded(stops: RouteStop[]): RouteStop[] {
  return [...stops].sort((a, b) => {
    const ta = parseStartMinutes(a.scheduledStartTime)
    const tb = parseStartMinutes(b.scheduledStartTime)
    if (ta == null && tb == null) return 0
    if (ta == null) return 1
    if (tb == null) return -1
    return ta - tb
  })
}

/**
 * Optimize one geographic band. Stops without coordinates go at the end of the
 * band (earlier scheduled_start_time first when present).
 */
function optimizeBand(stops: RouteStop[], start?: LatLng | null): string[] {
  if (stops.length <= 1) return stops.map((s) => s.id)

  const withCoords = stops.filter((s) => isFiniteLatLng(s.latitude, s.longitude))
  const withoutCoords = sortUncoded(
    stops.filter((s) => !isFiniteLatLng(s.latitude, s.longitude))
  )

  if (withCoords.length === 0) {
    return withoutCoords.map((s) => s.id)
  }

  const hasStart =
    start != null && isFiniteLatLng(start.latitude, start.longitude)

  const seedStops: RouteStop[] = hasStart
    ? [
        {
          id: ORIGIN_ID,
          latitude: start!.latitude,
          longitude: start!.longitude,
        },
        ...withCoords,
      ]
    : withCoords

  if (seedStops.length <= 1) {
    return [...seedStops, ...withoutCoords].map((s) => s.id)
  }

  const seeded = nearestNeighbor(seedStops)
  const improved = twoOpt(seeded)
  const ordered = improved.filter((s) => s.id !== ORIGIN_ID)
  return [...ordered, ...withoutCoords].map((s) => s.id)
}

/**
 * Optimize stop order. Stops without coordinates keep relative order at the end
 * of their band (or overall end when unbanded).
 * Optional `start` is a virtual origin (current GPS) — not included in output.
 * Nearest-neighbor from that origin, then 2-opt on the path (origin stays fixed).
 *
 * When any stop has `eveningPreferred`, runs two-phase:
 * 1) non-DSNY / hallway from GPS
 * 2) DSNY from the last non-DSNY stop (or GPS if none)
 */
export function optimizeRouteOrder(
  stops: RouteStop[],
  start?: LatLng | null
): string[] {
  if (stops.length <= 1) return stops.map((s) => s.id)

  const hasEveningBand = stops.some((s) => s.eveningPreferred === true)
  if (!hasEveningBand) {
    return optimizeBand(stops, start)
  }

  const early = stops.filter((s) => !s.eveningPreferred)
  const late = stops.filter((s) => s.eveningPreferred)
  const earlyIds = optimizeBand(early, start)
  const byId = new Map(stops.map((s) => [s.id, s]))
  const lateStart = lastStopWithCoords(earlyIds, byId) || start || null
  const lateIds = optimizeBand(late, lateStart)
  return [...earlyIds, ...lateIds]
}

/** Closest stop id to an origin; ignores stops without coordinates. */
export function pickClosestStopId(
  origin: LatLng,
  stops: RouteStop[]
): string | null {
  let bestId: string | null = null
  let bestDist = Number.POSITIVE_INFINITY
  for (const stop of stops) {
    if (
      stop.latitude == null ||
      stop.longitude == null ||
      !Number.isFinite(stop.latitude) ||
      !Number.isFinite(stop.longitude)
    ) {
      continue
    }
    const d = haversineKm(
      origin.latitude,
      origin.longitude,
      stop.latitude,
      stop.longitude
    )
    if (d < bestDist) {
      bestDist = d
      bestId = stop.id
    }
  }
  return bestId
}

/** Distance in km, or null if either point lacks coords. */
export function distanceKmBetween(
  a: LatLng | null | undefined,
  b: LatLng | null | undefined
): number | null {
  if (
    !a ||
    !b ||
    !Number.isFinite(a.latitude) ||
    !Number.isFinite(a.longitude) ||
    !Number.isFinite(b.latitude) ||
    !Number.isFinite(b.longitude)
  ) {
    return null
  }
  return haversineKm(a.latitude, a.longitude, b.latitude, b.longitude)
}

/** Google Maps directions URL (no API key). Max ~25 waypoints in practice. */
export function googleMapsDirectionsUrl(
  stops: Array<{ latitude: number; longitude: number }>,
  origin?: LatLng | null
): string | null {
  if (stops.length === 0) return null

  const hasOrigin = origin != null && isFiniteLatLng(origin.latitude, origin.longitude)
  const destination = `${stops[stops.length - 1].latitude},${stops[stops.length - 1].longitude}`

  if (!hasOrigin && stops.length === 1) {
    return `https://www.google.com/maps/dir/?api=1&destination=${destination}&travelmode=driving`
  }

  const originStr = hasOrigin
    ? `${origin!.latitude},${origin!.longitude}`
    : `${stops[0].latitude},${stops[0].longitude}`
  const waypointStops = hasOrigin ? stops.slice(0, -1) : stops.slice(1, -1)
  const waypoints = waypointStops
    .slice(0, 23)
    .map((s) => `${s.latitude},${s.longitude}`)
    .join('|')

  let url = `https://www.google.com/maps/dir/?api=1&origin=${originStr}&destination=${destination}`
  if (waypoints) url += `&waypoints=${waypoints}`
  url += `&travelmode=driving`
  return url
}
